import { useEffect, useRef, useState } from 'react';
import jsQR from 'jsqr';

const DEFAULT_INTERVAL_MS = 800;
const DEFAULT_GRACE_MS = 6000;
const MAX_SAMPLE_WIDTH = 640;

const isHttpUrl = (text) => {
    if (!text || typeof text !== 'string') return false;
    const trimmed = text.trim();
    if (!/^https?:\/\//i.test(trimmed)) return false;
    try {
        // eslint-disable-next-line no-new
        new URL(trimmed);
        return true;
    } catch (e) {
        return false;
    }
};

// Locates the OpenTok-injected <video> element inside a container that may not
// exist yet when the hook mounts. Returns null until OT renders the subscriber.
const findVideoElement = (container) => {
    if (!container) return null;
    const video = container.querySelector('video');
    if (!video) return null;
    if (video.readyState < 2) return null;
    if (!video.videoWidth || !video.videoHeight) return null;
    return video;
};

const useQRDetection = ({
    containerId,
    enabled = true,
    intervalMs = DEFAULT_INTERVAL_MS,
    graceMs = DEFAULT_GRACE_MS,
}) => {
    const [url, setUrl] = useState(null);
    const lastDetectedAtRef = useRef(0);
    const canvasRef = useRef(null);
    const dismissedUrlsRef = useRef(new Set());

    useEffect(() => {
        if (!enabled || !containerId) return undefined;

        let cancelled = false;
        let intervalHandle = null;

        const scan = () => {
            if (cancelled) return;

            const container = document.getElementById(containerId);
            const video = findVideoElement(container);

            // Clear button if grace period elapsed with no detections
            if (!video) {
                if (
                    lastDetectedAtRef.current &&
                    Date.now() - lastDetectedAtRef.current > graceMs
                ) {
                    setUrl(null);
                }
                return;
            }

            const vw = video.videoWidth;
            const vh = video.videoHeight;
            const scale = Math.min(1, MAX_SAMPLE_WIDTH / vw);
            const w = Math.max(1, Math.floor(vw * scale));
            const h = Math.max(1, Math.floor(vh * scale));

            if (!canvasRef.current) {
                canvasRef.current = document.createElement('canvas');
            }
            const canvas = canvasRef.current;
            if (canvas.width !== w) canvas.width = w;
            if (canvas.height !== h) canvas.height = h;

            const ctx = canvas.getContext('2d', { willReadFrequently: true });
            if (!ctx) return;

            try {
                ctx.drawImage(video, 0, 0, w, h);
            } catch (err) {
                // Some browsers throw if the video frame isn't ready yet.
                return;
            }

            let imageData;
            try {
                imageData = ctx.getImageData(0, 0, w, h);
            } catch (err) {
                // Cross-origin or tainted canvas — bail silently.
                return;
            }

            const result = jsQR(imageData.data, w, h, {
                inversionAttempts: 'dontInvert',
            });

            if (result && isHttpUrl(result.data)) {
                const detectedUrl = result.data.trim();
                if (!dismissedUrlsRef.current.has(detectedUrl)) {
                    lastDetectedAtRef.current = Date.now();
                    setUrl((prev) => (prev === detectedUrl ? prev : detectedUrl));
                }
            } else if (
                lastDetectedAtRef.current &&
                Date.now() - lastDetectedAtRef.current > graceMs
            ) {
                setUrl(null);
            }
        };

        intervalHandle = setInterval(scan, intervalMs);
        scan();

        return () => {
            cancelled = true;
            if (intervalHandle) clearInterval(intervalHandle);
        };
    }, [containerId, enabled, intervalMs, graceMs]);

    const dismiss = () => {
        if (url) {
            dismissedUrlsRef.current.add(url);
        }
        lastDetectedAtRef.current = 0;
        setUrl(null);
    };

    return { url, dismiss };
};

export default useQRDetection;
